import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { get, getOr, isNaN } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Table, Icon, InputGroup, Input } from 'rsuite';
import { OrderItemType } from '../../../../../../interfaces/pos';
import { parseInt } from '../../../../../../helpers/utils';

const { Cell } = Table;

const StyledQtyInput = styled(Input)`
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
  }
  -moz-appearance: textfield;
`;

export const QuantityCell = ({
  dataKey,
  rowData,
  onChange,
  isDeleting,
  isItemHasError,
  ...props
}) => {
  const itemId = get('inventoryItemId', rowData);
  const locationId = get('inventoryLocationId', rowData);
  const zoneId = get('inventoryLocationZoneId', rowData);
  const defaultQty = parseInt(getOr(0, 'quantity', rowData));
  const [quantity, setQuantity] = useState(defaultQty);
  const isMinQuantity = quantity <= 1;
  const isDeletingItem = isDeleting(rowData);

  useEffect(
    () => {
      if (itemId && locationId) setQuantity(defaultQty);
    },
    [itemId, locationId, zoneId, defaultQty]
  );

  const handleOnChange = value => {
    const intVal = parseInt(value);
    if (!isNaN(intVal) && intVal > 0) {
      onChange(rowData, intVal);
      setQuantity(intVal);
    } else {
      setQuantity('');
    }
  };

  const handleOnBlur = () => {
    if (!quantity) {
      handleOnChange(defaultQty);
    }
  };

  const handleOnIncrement = () => {
    if (isDeletingItem) return;
    setQuantity(quantity + 1);
    onChange(rowData, quantity + 1);
  };

  const handleOnDecrement = () => {
    if (isMinQuantity || isDeletingItem) return;
    setQuantity(quantity - 1);
    onChange(rowData, quantity - 1);
  };

  const hasError = () => isItemHasError(itemId, locationId, zoneId);

  return (
    <Cell {...props}>
      <InputGroup>
        <InputGroup.Button
          disabled={isMinQuantity || isDeletingItem}
          color="red"
          appearance="ghost"
          onClick={handleOnDecrement}
        >
          <Icon icon="minus" />
        </InputGroup.Button>
        <StyledQtyInput
          disabled={isDeletingItem}
          className={hasError() && 'rs-message-error'}
          min={1}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          value={quantity}
          width={80}
          type="number"
          style={{ textAlign: 'center' }}
        />
        <InputGroup.Button
          disabled={isDeletingItem}
          color="green"
          appearance="ghost"
          onClick={handleOnIncrement}
        >
          <Icon icon="plus" />
        </InputGroup.Button>
      </InputGroup>
    </Cell>
  );
};

QuantityCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  isDeleting: PropTypes.func.isRequired,
  isItemHasError: PropTypes.func.isRequired,
  rowData: OrderItemType
};

QuantityCell.defaultProps = {
  rowData: null
};

export default QuantityCell;
